import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import PageMetadata from '../../components/PageMetadata';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import ChatList from './ChatList';
import ChatMessages from './ChatMessages';
import DeleteConfirmModal from '../../components/MessageActionsModal/DeleteConfirmModal';
import { UsersFilter } from '../../components/UsersFilter';
import {
  getChats,
  createChat,
  updateChat,
  deleteChat as deleteChatApi,
  getChatMessages,
  sendChatMessage,
  deleteChatMessage,
} from '../../services/internalChatService';
import toastError from '../../errors/toastError';
import has from 'lodash/has';
import isObject from 'lodash/isObject';
import { AuthContext } from '../../context/Auth/AuthContext';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import { i18n } from '../../translate/i18n';
import { getInternalChatDeleteMessagePreview } from '../../utils/internalChatMedia';
import { appendInternalChatMessage, upsertInternalChatInList } from '../../utils/internalChatSync';
import { playInternalChatNotificationSound } from '../../utils/notificationSound';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import ForumIcon from '@material-ui/icons/Forum';
import CloseIcon from '@material-ui/icons/Close';

const useChatModalStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    overflow: 'hidden',
    maxHeight: 'min(90vh, 640px)',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    boxShadow:
      theme.palette.type === 'dark' ? theme.shadows[24] : '0 8px 32px rgba(15, 23, 42, 0.14)',
    backgroundColor: theme.palette.background.paper,
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2.25),
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    '& svg': {
      fontSize: 22,
      color: theme.palette.primary.main,
    },
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
    lineHeight: 1.35,
  },
  headerCloseBtn: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1),
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
    },
  },
  dialogContent: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2, 2, 2.5),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f8fafc',
  },
  sectionCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e2e8f0'}`,
    padding: theme.spacing(2, 2),
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  sectionTitle: {
    fontSize: '0.8125rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.75),
    paddingLeft: theme.spacing(1.25),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  },
  field: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': { borderRadius: 10 },
  },
  dialogActions: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  btn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    minWidth: 100,
  },
}));

const useStyles = makeStyles((theme) => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
    padding: theme.spacing(0),
    backgroundColor: theme.palette.background.default,
  },
  shell: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    alignSelf: 'stretch',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow:
      theme.palette.type === 'dark'
        ? '0 4px 24px rgba(0,0,0,0.35)'
        : '0 4px 24px rgba(15, 23, 42, 0.08)',
    [theme.breakpoints.down('sm')]: {
      maxHeight: 'none',
      borderRadius: 0,
      border: 'none',
      boxShadow: 'none',
    },
  },
  shellColumn: {
    flexDirection: 'column',
  },
  sidebar: {
    width: 360,
    flexShrink: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  main: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  emptyMain: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      borderLeft: 'none',
    },
  },
  emptyIcon: {
    fontSize: 56,
    color: theme.palette.action.active,
    marginBottom: theme.spacing(2),
    opacity: 0.5,
  },
  gridTab: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

export function ChatModal({ open, chat, type, handleClose, handleLoadNewChat }) {
  const modalClasses = useChatModalStyles();
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState('');

  useEffect(() => {
    setTitle('');
    setUsers([]);
    setTitleError('');
    if (type === 'edit' && chat?.users) {
      const userList = chat.users.map((u) => ({
        id: u.user.id,
        name: u.user.name,
      }));
      setUsers(userList);
      setTitle(chat.title);
    }
  }, [chat, open, type]);

  const handleSave = async () => {
    const trimmed = (title || '').trim();
    if (!trimmed) {
      setTitleError(i18n.t('chatInternal.modal.titleRequired'));
      return;
    }
    setTitleError('');
    try {
      if (type === 'edit') {
        await updateChat(chat.id, {
          users,
          title: trimmed,
        });
      } else {
        const { data } = await createChat({
          users,
          title: trimmed,
        });
        handleLoadNewChat(data);
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const modalSubtitle =
    type === 'edit'
      ? 'Atualize o título e os participantes desta conversa.'
      : 'Defina um título e escolha quem participa do chat interno.';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="chat-internal-dialog-title"
      PaperProps={{ className: modalClasses.dialogPaper }}
    >
      <Box className={modalClasses.dialogHeader} component="header">
        <Box className={modalClasses.headerLeft}>
          <Box className={modalClasses.headerIcon} aria-hidden>
            <ForumIcon />
          </Box>
          <Box minWidth={0}>
            <Typography
              component="h2"
              className={modalClasses.headerTitle}
              id="chat-internal-dialog-title"
            >
              {i18n.t('chatInternal.modal.title')}
            </Typography>
            <Typography className={modalClasses.headerSubtitle}>{modalSubtitle}</Typography>
          </Box>
        </Box>
        <Tooltip title="Fechar">
          <IconButton
            size="small"
            onClick={handleClose}
            className={modalClasses.headerCloseBtn}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <DialogContent className={modalClasses.dialogContent}>
        <Box className={modalClasses.sectionCard}>
          <Typography component="h3" className={modalClasses.sectionTitle}>
            Dados da conversa
          </Typography>
          <TextField
            label="Título"
            placeholder="Ex.: Equipe comercial, Suporte N2…"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError) setTitleError('');
            }}
            variant="outlined"
            size="small"
            fullWidth
            className={modalClasses.field}
            error={Boolean(titleError)}
            helperText={titleError || undefined}
          />
          <UsersFilter onFiltered={(u) => setUsers(u)} initialUsers={users} />
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginTop: 8 }}
          >
            Selecione ao menos um participante além de você, quando aplicável.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions className={modalClasses.dialogActions}>
        <Button onClick={handleClose} startIcon={<CancelIcon />} className={modalClasses.btn}>
          {i18n.t('chatInternal.modal.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<SaveIcon />}
          color="primary"
          variant="contained"
          className={modalClasses.btn}
          disabled={users === undefined || users.length === 0}
        >
          {i18n.t('chatInternal.modal.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Chat(props) {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const history = useHistory();

  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState('new');
  const [currentChat, setCurrentChat] = useState({});
  const [chats, setChats] = useState([]);
  const [chatsPageInfo, setChatsPageInfo] = useState({ hasMore: false });
  const [messages, setMessages] = useState([]);
  const [messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [messagesPage, setMessagesPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleteMessageModalOpen, setDeleteMessageModalOpen] = useState(false);
  const [pendingDeleteMessageId, setPendingDeleteMessageId] = useState(null);
  const [tab, setTab] = useState(0);
  const [typingLabel, setTypingLabel] = useState(null);
  const isMounted = useRef(true);
  const scrollToBottomRef = useRef();
  const typingTimerRef = useRef(null);
  const currentChatIdRef = useRef(null);
  const { id } = useParams();

  const markChatReadLocal = useCallback(
    (chatId) => {
      if (chatId == null) return;
      setChats((prev) =>
        prev.map((c) => {
          if (String(c.id) !== String(chatId) || !Array.isArray(c.users)) return c;
          return {
            ...c,
            users: c.users.map((u) =>
              String(u.userId) === String(user.id) ? { ...u, unreads: 0 } : u
            ),
          };
        })
      );
      setCurrentChat((cur) =>
        cur?.id != null && String(cur.id) === String(chatId) && Array.isArray(cur.users)
          ? {
              ...cur,
              users: cur.users.map((u) =>
                String(u.userId) === String(user.id) ? { ...u, unreads: 0 } : u
              ),
            }
          : cur
      );
    },
    [user.id]
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
      clearTimeout(typingTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setTypingLabel(null);
    clearTimeout(typingTimerRef.current);
  }, [currentChat?.id]);

  useEffect(() => {
    if (isMounted.current) {
      findChats().then((data) => {
        const records = data?.records || [];
        setChats(records);
        setChatsPageInfo(data);
        if (id && records.length) {
          const chat = records.find((r) => r.uuid === id);
          if (chat) selectChat(chat);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    currentChatIdRef.current =
      isObject(currentChat) && has(currentChat, 'id') ? currentChat.id : null;
  }, [currentChat]);

  useEffect(() => {
    if (isObject(currentChat) && has(currentChat, 'id')) {
      findMessagesFirstPage(currentChat.id).then(() => {
        if (typeof scrollToBottomRef.current === 'function') {
          setTimeout(() => {
            scrollToBottomRef.current();
          }, 300);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat]);

  useEffect(() => {
    const companyId = user.companyId;
    const activeChatId = isObject(currentChat) && has(currentChat, 'id') ? currentChat.id : null;

    const onChatUser = (data) => {
      if (data.action === 'create' && data.record) {
        setChats((prev) => upsertInternalChatInList(prev, data.record));
      }
      if (data.action === 'update' && data.record) {
        setChats((prev) => upsertInternalChatInList(prev, data.record));
        setCurrentChat((cur) =>
          cur?.id != null && String(cur.id) === String(data.record.id)
            ? { ...cur, ...data.record }
            : cur
        );
      }
    };
    const applyDeleteMessageFromSocket = (data) => {
      if (data?.action !== 'delete-message' || data.messageId == null) return;
      const messageId = data.messageId;
      const chatPayload = data.chat;
      if (chatPayload?.id != null) {
        setChats((prev) =>
          prev.map((c) => (String(c.id) === String(chatPayload.id) ? { ...chatPayload } : c))
        );
        setCurrentChat((cur) =>
          cur?.id != null && String(cur.id) === String(chatPayload.id) ? { ...chatPayload } : cur
        );
      }
      if (
        isObject(currentChat) &&
        has(currentChat, 'id') &&
        chatPayload?.id != null &&
        String(chatPayload.id) === String(currentChat.id)
      ) {
        setMessages((prev) => prev.filter((m) => String(m.id) !== String(messageId)));
      }
    };

    const onChat = (data) => {
      if (
        data?.action === 'typing' &&
        data?.userId &&
        String(data.userId) !== String(user.id) &&
        isObject(currentChat) &&
        has(currentChat, 'id') &&
        String(data.chatId || data?.chat?.id) === String(currentChat.id)
      ) {
        setTypingLabel(data.userName || 'Alguém');
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingLabel(null), 3000);
        return;
      }
      if (data.action === 'delete' && data.id != null) {
        const deletedId = data.id;
        setChats((prev) => prev.filter((c) => String(c.id) !== String(deletedId)));
        if (
          isObject(currentChat) &&
          has(currentChat, 'id') &&
          String(currentChat.id) === String(deletedId)
        ) {
          setMessages([]);
          setMessagesPage(1);
          setMessagesPageInfo({ hasMore: false });
          setCurrentChat({});
          history.push('/chats');
        }
        return;
      }
      if ((data.action === 'new-message' || data.action === 'update') && data.chat) {
        setChats((prev) => upsertInternalChatInList(prev, data.chat));
        if (
          isObject(currentChat) &&
          has(currentChat, 'id') &&
          String(data.chat.id) === String(currentChat.id)
        ) {
          setCurrentChat((cur) => ({ ...cur, ...data.chat }));
        }
        if (
          data.action === 'new-message' &&
          data.newMessage &&
          String(data.newMessage.senderId) !== String(user.id) &&
          String(data.chat.id) !== String(currentChat?.id)
        ) {
          playInternalChatNotificationSound();
        }
      }
      applyDeleteMessageFromSocket(data);
    };

    const onCurrentChat = (data) => {
      if (data.action === 'typing' && data.userId && String(data.userId) !== String(user.id)) {
        setTypingLabel(data.userName || 'Alguém');
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingLabel(null), 3000);
        return;
      }

      if (data.action === 'new-message' && data.newMessage) {
        clearTimeout(typingTimerRef.current);
        setTypingLabel(null);
        setMessages((prev) => appendInternalChatMessage(prev, data.newMessage));
        if (data.chat) {
          setChats((prev) =>
            prev.map((c) =>
              String(c.id) === String(data.newMessage.chatId) ? { ...data.chat } : c
            )
          );
          setCurrentChat((cur) =>
            cur?.id != null && String(cur.id) === String(data.chat.id)
              ? { ...cur, ...data.chat }
              : cur
          );
        }
        if (String(data.newMessage.senderId) !== String(user.id)) {
          playInternalChatNotificationSound();
        }
        scrollToBottomRef.current?.();
      }

      if (data.action === 'update' && data.chat) {
        setChats((prev) =>
          prev.map((c) => (String(c.id) === String(data.chat.id) ? { ...data.chat } : c))
        );
        setCurrentChat((cur) =>
          cur?.id != null && String(cur.id) === String(data.chat.id)
            ? { ...cur, ...data.chat }
            : cur
        );
        scrollToBottomRef.current?.();
      }

      applyDeleteMessageFromSocket(data);
    };

    socket.on(`company-${companyId}-chat-user-${user.id}`, onChatUser);
    socket.on(`company-${companyId}-chat`, onChat);
    if (activeChatId) {
      socket.on(`company-${companyId}-chat-${activeChatId}`, onCurrentChat);
    }

    return () => {
      socket.off(`company-${companyId}-chat-user-${user.id}`, onChatUser);
      socket.off(`company-${companyId}-chat`, onChat);
      if (activeChatId) {
        socket.off(`company-${companyId}-chat-${activeChatId}`, onCurrentChat);
      }
    };
  }, [currentChat?.id, user?.companyId, user?.id]);

  const selectChat = (chat) => {
    try {
      setMessages([]);
      setMessagesPage(1);
      setCurrentChat(chat);
      setTab(1);
    } catch (err) {
      /* ignore */
    }
  };

  const clearActiveChat = () => {
    setCurrentChat({});
    setMessages([]);
    setMessagesPage(1);
    setMessagesPageInfo({ hasMore: false });
    setTab(0);
    history.push('/chats');
  };

  const sendMessage = async (contentMessage, attachmentFile) => {
    setLoading(true);
    try {
      let created;
      if (attachmentFile instanceof File) {
        const fd = new FormData();
        fd.append('message', typeof contentMessage === 'string' ? contentMessage.trim() : '');
        fd.append('medias', attachmentFile, attachmentFile.name);
        ({ data: created } = await sendChatMessage(currentChat.id, fd));
      } else {
        ({ data: created } = await sendChatMessage(currentChat.id, {
          message: contentMessage,
        }));
      }
      const newMessage = created?.newMessage || created?.record || created;
      if (newMessage?.id != null) {
        setMessages((prev) => appendInternalChatMessage(prev, newMessage));
      }
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const requestDeleteMessage = (messageId) => {
    if (!isObject(currentChat) || !has(currentChat, 'id') || messageId == null) return;
    setPendingDeleteMessageId(messageId);
    setDeleteMessageModalOpen(true);
  };

  const handleDeleteMessageModalClose = () => {
    setDeleteMessageModalOpen(false);
    setPendingDeleteMessageId(null);
  };

  const confirmDeleteMessage = async () => {
    const messageId = pendingDeleteMessageId;
    if (!isObject(currentChat) || !has(currentChat, 'id') || messageId == null) {
      handleDeleteMessageModalClose();
      return;
    }
    setDeleteMessageModalOpen(false);
    setPendingDeleteMessageId(null);
    setLoading(true);
    try {
      await deleteChatMessage(currentChat.id, messageId);
      setMessages((prev) => prev.filter((m) => String(m.id) !== String(messageId)));
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const deleteChat = async (chat) => {
    try {
      await deleteChatApi(chat.id);
    } catch (err) {
      toastError(err);
    }
  };

  const findMessagesFirstPage = async (chatId) => {
    setLoading(true);
    try {
      const { data } = await getChatMessages(chatId, { pageNumber: 1 });
      setMessagesPage(2);
      setMessagesPageInfo(data);
      setMessages([...(data.records || [])]);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const findMessagesNextPage = async (chatId) => {
    setLoading(true);
    try {
      const { data } = await getChatMessages(chatId, { pageNumber: messagesPage });
      setMessagesPage((prev) => prev + 1);
      setMessagesPageInfo(data);
      setMessages((prev) => [...(data.records || []), ...prev]);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const loadMoreMessages = async () => {
    if (!loading && currentChat?.id) {
      findMessagesNextPage(currentChat.id);
    }
  };

  const findChats = async () => {
    try {
      const { data } = await getChats();
      return data;
    } catch (err) {
      toastError(err);
      return { records: [], hasMore: false };
    }
  };

  const openNewChat = () => {
    setDialogType('new');
    setShowDialog(true);
  };

  const renderEmptyMain = () => (
    <Box className={classes.emptyMain}>
      <ForumIcon className={classes.emptyIcon} />
      <Typography variant="h6" gutterBottom>
        Conversas internas
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ maxWidth: 360 }}>
        Selecione uma conversa na lista ao lado ou crie uma nova para começar a trocar mensagens com
        a equipe.
      </Typography>
      <Box mt={2}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNewChat}>
          {i18n.t('chatInternal.new')}
        </Button>
      </Box>
    </Box>
  );

  const renderGrid = () => {
    return (
      <Box className={classes.shell} display="flex" flexDirection="row">
        <Box className={classes.sidebar}>
          <ChatList
            chats={chats}
            pageInfo={chatsPageInfo}
            loading={loading}
            onNewChat={openNewChat}
            onChatReadMarked={markChatReadLocal}
            handleSelectChat={(chat) => selectChat(chat)}
            handleDeleteChat={(chat) => deleteChat(chat)}
            handleEditChat={(chat) => {
              if (chat) selectChat(chat);
              setDialogType('edit');
              setShowDialog(true);
            }}
          />
        </Box>
        <Box className={classes.main}>
          {isObject(currentChat) && has(currentChat, 'id') ? (
            <ChatMessages
              chat={currentChat}
              onBack={clearActiveChat}
              scrollToBottomRef={scrollToBottomRef}
              pageInfo={messagesPageInfo}
              messages={messages}
              loading={loading}
              handleSendMessage={sendMessage}
              handleLoadMore={loadMoreMessages}
              handleDeleteMessage={requestDeleteMessage}
              typingLabel={typingLabel}
            />
          ) : (
            renderEmptyMain()
          )}
        </Box>
      </Box>
    );
  };

  const renderTab = () => {
    return (
      <Paper className={`${classes.shell} ${classes.shellColumn}`} elevation={0}>
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(e, v) => setTab(v)}
          aria-label="Abas do chat interno"
        >
          <Tab label="Lista" />
          <Tab label="Mensagens" disabled={!isObject(currentChat) || !has(currentChat, 'id')} />
        </Tabs>
        {tab === 0 && (
          <Box className={classes.gridTab}>
            <ChatList
              chats={chats}
              pageInfo={chatsPageInfo}
              loading={loading}
              onNewChat={openNewChat}
              onChatReadMarked={markChatReadLocal}
              handleSelectChat={(chat) => selectChat(chat)}
              handleDeleteChat={(chat) => deleteChat(chat)}
              handleEditChat={(chat) => {
                if (chat) selectChat(chat);
                setDialogType('edit');
                setShowDialog(true);
              }}
            />
          </Box>
        )}
        {tab === 1 && (
          <Box className={classes.gridTab}>
            {isObject(currentChat) && has(currentChat, 'id') ? (
              <ChatMessages
                chat={currentChat}
                onBack={clearActiveChat}
                scrollToBottomRef={scrollToBottomRef}
                pageInfo={messagesPageInfo}
                messages={messages}
                loading={loading}
                handleSendMessage={sendMessage}
                handleLoadMore={loadMoreMessages}
                handleDeleteMessage={requestDeleteMessage}
                typingLabel={typingLabel}
              />
            ) : (
              renderEmptyMain()
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const pendingDeletePreview = useMemo(() => {
    const m = messages.find((x) => String(x.id) === String(pendingDeleteMessageId));
    if (!m) return '';
    return getInternalChatDeleteMessagePreview(m, {
      audioLabel: i18n.t('chatInternal.deleteModal.previewAudio'),
      imageLabel: i18n.t('chatInternal.deleteModal.previewImage'),
      videoLabel: i18n.t('chatInternal.deleteModal.previewVideo'),
      documentLabel: i18n.t('chatInternal.deleteModal.previewDocument'),
    });
  }, [messages, pendingDeleteMessageId]);

  return (
    <>
      <PageMetadata title="Chat interno" />
      <DeleteConfirmModal
        open={deleteMessageModalOpen}
        onClose={handleDeleteMessageModalClose}
        onConfirm={confirmDeleteMessage}
        messageText={pendingDeletePreview}
      />
      <ChatModal
        type={dialogType}
        open={showDialog}
        chat={currentChat}
        handleLoadNewChat={(data) => {
          setMessages([]);
          setMessagesPage(1);
          setCurrentChat(data);
          setChats((prev) => {
            if (prev.some((c) => c.id === data.id)) {
              return prev.map((c) => (c.id === data.id ? data : c));
            }
            return [data, ...prev];
          });
          setTab(1);
          history.push(`/chats/${data.uuid}`);
        }}
        handleClose={() => setShowDialog(false)}
      />
      <Box className={classes.page}>
        {isWidthUp('md', props.width) ? renderGrid() : renderTab()}
      </Box>
    </>
  );
}

export default withWidth()(Chat);
